import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  type AuthState,
  type CategoryRecord,
  type DailyNoteRecord,
  type EventRecord,
  type EventWritePayload,
  type TaskRecord,
  type TaskUpdatePayload,
} from './api';
import { CalendarView } from './components/CalendarView';
import { CommandBar } from './components/CommandBar';
import { EventDetail, type EventEditorDraft } from './components/EventDetail';
import { LoginPage } from './components/LoginPage';
import { NotesView } from './components/NotesView';
import { ReviewView } from './components/ReviewView';
import { TaskDetail } from './components/TaskDetail';
import { TaskSidebar } from './components/TaskSidebar';
import { ToastContainer } from './components/Toast';
import { TodayView } from './components/TodayView';
import { addDays, formatDateKey } from './utils/date';

type ToastItem = { id: number; message: string; type: 'success' | 'error' };
type AppView = 'today' | 'calendar' | 'notes' | 'review';

export default function App() {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [categories, setCategories] = useState<CategoryRecord[]>([]);
  const [todayNote, setTodayNote] = useState<DailyNoteRecord | null>(null);
  const [recentNotes, setRecentNotes] = useState<DailyNoteRecord[]>([]);
  const [activeNoteDate, setActiveNoteDate] = useState(formatDateKey(new Date()));
  const [activeNote, setActiveNote] = useState<DailyNoteRecord | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskRecord | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventRecord | null>(null);
  const [eventDraft, setEventDraft] = useState<EventEditorDraft | null>(null);
  const [commandBarOpen, setCommandBarOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [view, setView] = useState<AppView>(() => getViewFromHash(window.location.hash));
  const [dataLoading, setDataLoading] = useState(false);
  const [calendarRange, setCalendarRange] = useState(getDefaultCalendarRange());
  const toastIdRef = useRef(0);

  const toast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((item) => item.id !== id)), 3000);
  }, []);

  const refreshAuth = useCallback(async () => {
    const res = await api.auth.me();
    setAuthState(res.data);
    return res.data;
  }, []);

  useEffect(() => {
    refreshAuth().catch(() => {
      setAuthState({
        authenticated: false,
        authType: null,
        bootstrapRequired: false,
        sessionStage: null,
        user: null,
        agent: null,
      });
    });
  }, [refreshAuth]);

  const refreshRecentNotes = useCallback(async () => {
    const todayKey = formatDateKey(new Date());
    const recentRes = await api.dailyNotes.list({
      start: addDays(todayKey, -13),
      end: todayKey,
      limit: '14',
    });
    setRecentNotes(recentRes.data);
  }, []);

  const refreshWorkspace = useCallback(async () => {
    setDataLoading(true);
    const todayKey = formatDateKey(new Date());

    try {
      const [taskRes, categoryRes, eventRes, todayNoteRes, recentNoteRes] = await Promise.all([
        api.tasks.list({ limit: '250' }),
        api.categories.list(),
        api.events.list(calendarRange.start, calendarRange.end),
        api.dailyNotes.get(todayKey),
        api.dailyNotes.list({ start: addDays(todayKey, -13), end: todayKey, limit: '14' }),
      ]);

      setTasks(taskRes.data || []);
      setCategories(categoryRes.data || []);
      setEvents(eventRes.data || []);
      setTodayNote(todayNoteRes.data);
      setRecentNotes(recentNoteRes.data || []);

      setSelectedTask((prev) => prev ? taskRes.data.find((task) => task.id === prev.id) ?? null : null);
      setSelectedEvent((prev) => prev ? eventRes.data.find((event) => event.id === prev.id) ?? null : null);

      if (activeNoteDate === todayKey) {
        setActiveNote(todayNoteRes.data);
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not load workspace', 'error');
    } finally {
      setDataLoading(false);
    }
  }, [activeNoteDate, calendarRange.end, calendarRange.start, toast]);

  const loadActiveNote = useCallback(async (date: string) => {
    try {
      const result = await api.dailyNotes.get(date);
      setActiveNote(result.data);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not load note', 'error');
    }
  }, [toast]);

  useEffect(() => {
    if (authState?.authenticated) {
      void refreshWorkspace();
    }
  }, [authState?.authenticated, refreshWorkspace]);

  useEffect(() => {
    if (!authState?.authenticated) {
      return;
    }

    const todayKey = formatDateKey(new Date());
    if (activeNoteDate === todayKey) {
      setActiveNote(todayNote);
      return;
    }

    void loadActiveNote(activeNoteDate);
  }, [activeNoteDate, authState?.authenticated, loadActiveNote, todayNote]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handler = () => {
      setView(getViewFromHash(window.location.hash));
    };

    if (!window.location.hash) {
      window.location.hash = '#today';
    }

    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setCommandBarOpen((prev) => !prev);
      }

      if (event.key === 'Escape') {
        setCommandBarOpen(false);
        setSelectedTask(null);
        setSelectedEvent(null);
        setEventDraft(null);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleToggleTask = async (task: TaskRecord) => {
    try {
      if (task.status === 'done') {
        await api.tasks.update(task.id, { status: 'pending' });
        toast('Task reopened');
      } else {
        await api.tasks.complete(task.id);
        toast('Task completed');
      }
      await refreshWorkspace();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not update task', 'error');
    }
  };

  const handleAddTask = async (title: string) => {
    try {
      await api.tasks.create({ title });
      toast('Inbox task created');
      await refreshWorkspace();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not create task', 'error');
    }
  };

  const handleUpdateTask = async (id: string, updates: TaskUpdatePayload) => {
    try {
      await api.tasks.update(id, updates);
      await refreshWorkspace();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not update task', 'error');
    }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      await api.tasks.delete(id);
      if (selectedTask?.id === id) {
        setSelectedTask(null);
      }
      toast('Task deleted');
      await refreshWorkspace();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not delete task', 'error');
    }
  };

  const handleSaveEvent = async (payload: EventWritePayload) => {
    try {
      if (selectedEvent) {
        await api.events.update(selectedEvent.id, payload);
        toast('Event updated');
      } else {
        await api.events.create(payload);
        toast('Event created');
      }
      setSelectedEvent(null);
      setEventDraft(null);
      await refreshWorkspace();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save event', 'error');
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) {
      return;
    }

    try {
      await api.events.delete(selectedEvent.id);
      setSelectedEvent(null);
      setEventDraft(null);
      toast('Event deleted');
      await refreshWorkspace();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not delete event', 'error');
    }
  };

  const handleMoveEvent = async (
    event: EventRecord,
    updates: { event_date: string; start_time: string | null; end_time: string | null; all_day: boolean },
  ) => {
    await api.events.update(event.id, updates);
    await refreshWorkspace();
    toast('Event rescheduled');
  };

  const handleMoveTask = async (
    task: TaskRecord,
    updates: { due_date: string; scheduled_start: string | null; scheduled_end: string | null },
  ) => {
    await api.tasks.update(task.id, updates);
    await refreshWorkspace();
    toast('Task rescheduled');
  };

  const handleSaveNote = async (date: string, updates: { title: string; content: string }) => {
    try {
      const result = await api.dailyNotes.upsert(date, updates);
      setActiveNote(result.data);
      if (date === formatDateKey(new Date())) {
        setTodayNote(result.data);
      }
      await refreshRecentNotes();
      toast('Note saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save note', 'error');
    }
  };

  const handleCaptureNote = async (text: string) => {
    const todayKey = formatDateKey(new Date());
    try {
      const result = await api.dailyNotes.append(todayKey, text);
      setTodayNote(result.data);
      if (activeNoteDate === todayKey) {
        setActiveNote(result.data);
      }
      await refreshRecentNotes();
      toast('Added to today\'s note');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not update note', 'error');
    }
  };

  const handleAddEventStub = async (title: string) => {
    try {
      await api.events.create({
        title,
        event_date: formatDateKey(new Date()),
        all_day: true,
        description: 'Captured from the command bar.',
      });
      window.location.hash = '#calendar';
      toast('Event stub created');
      await refreshWorkspace();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not create event', 'error');
    }
  };

  const handleLogout = async () => {
    await api.auth.logout();
    setTasks([]);
    setEvents([]);
    setCategories([]);
    setTodayNote(null);
    setRecentNotes([]);
    setSelectedTask(null);
    setSelectedEvent(null);
    setEventDraft(null);
    await refreshAuth();
  };

  const filteredTasks = useMemo(() => (
    [...tasks]
      .filter((task) => {
        if (statusFilter && task.status !== statusFilter) {
          return false;
        }
        if (categoryFilter && task.categoryName !== categoryFilter) {
          return false;
        }
        return true;
      })
      .toSorted((left, right) => {
        if (left.status === 'done' && right.status !== 'done') {
          return 1;
        }
        if (left.status !== 'done' && right.status === 'done') {
          return -1;
        }
        return (left.dueDate || '9999-12-31').localeCompare(right.dueDate || '9999-12-31');
      })
  ), [categoryFilter, statusFilter, tasks]);

  const statusCounts = useMemo(() => ({
    all: tasks.length,
    pending: tasks.filter((task) => task.status === 'pending').length,
    in_progress: tasks.filter((task) => task.status === 'in_progress').length,
    done: tasks.filter((task) => task.status === 'done').length,
    blocked: tasks.filter((task) => task.status === 'blocked').length,
  }), [tasks]);

  const completedThisWeek = useMemo(() => {
    const since = new Date();
    since.setDate(since.getDate() - 7);
    return tasks
      .filter((task) => task.completedAt && new Date(task.completedAt) >= since)
      .sort((left, right) => (right.completedAt || '').localeCompare(left.completedAt || ''));
  }, [tasks]);

  const overdueTasks = useMemo(() => {
    const todayKey = formatDateKey(new Date());
    return tasks.filter((task) => task.status !== 'done' && task.dueDate !== null && task.dueDate < todayKey);
  }, [tasks]);

  const notesThisWeek = useMemo(() => {
    const start = addDays(formatDateKey(new Date()), -6);
    return recentNotes.filter((note) => note.noteDate >= start);
  }, [recentNotes]);

  const headerNav = [
    { id: 'today', label: 'Today' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'notes', label: 'Notes' },
    { id: 'review', label: 'Review' },
  ] as const;

  if (authState === null) {
    return (
      <div className="login-page">
        <div className="login-card">Loading workspace...</div>
      </div>
    );
  }

  if (!authState.authenticated) {
    return <LoginPage authState={authState} onAuthenticated={refreshAuth} />;
  }

  return (
    <div className="app-layout">
      <header className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1>CI</h1>
          <nav style={{ display: 'flex', gap: 8 }}>
            {headerNav.map((item) => (
              <button
                key={item.id}
                className={`chip ${view === item.id ? 'active' : ''}`}
                onClick={() => {
                  window.location.hash = `#${item.id}`;
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="header-right">
          <span className="header-time">{authState.user?.username}</span>
          <span className="header-time">
            {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{' '}
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button className="header-kbd" onClick={() => setCommandBarOpen(true)}>⌘K</button>
          <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Sign Out</button>
        </div>
      </header>

      <TaskSidebar
        tasks={filteredTasks}
        categories={categories}
        statusFilter={statusFilter}
        categoryFilter={categoryFilter}
        statusCounts={statusCounts}
        onStatusFilter={setStatusFilter}
        onCategoryFilter={setCategoryFilter}
        onToggleTask={handleToggleTask}
        onSelectTask={(task) => {
          setSelectedEvent(null);
          setEventDraft(null);
          setSelectedTask(task);
        }}
        onAddTask={handleAddTask}
      />

      <div className="main-content">
        {dataLoading && tasks.length === 0 ? (
          <section
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: 24,
            }}
          >
            Loading your workspace...
          </section>
        ) : (
          renderMainView({
            view,
            tasks,
            events,
            activeNoteDate,
            activeNote,
            todayNote,
            recentNotes,
            completedThisWeek,
            overdueTasks,
            notesThisWeek,
            onSaveNote: handleSaveNote,
            onSelectTask: (task) => {
              setSelectedEvent(null);
              setEventDraft(null);
              setSelectedTask(task);
            },
            onSelectEvent: (event) => {
              setSelectedTask(null);
              setEventDraft(null);
              setSelectedEvent(event);
            },
            onOpenNotes: () => {
              window.location.hash = '#notes';
            },
            onOpenNote: (date) => {
              setActiveNoteDate(date);
              window.location.hash = '#notes';
            },
            onChangeNoteDate: setActiveNoteDate,
            onRequestCreateEvent: (draft) => {
              setSelectedTask(null);
              setSelectedEvent(null);
              setEventDraft(draft);
            },
            onMoveEvent: handleMoveEvent,
            onMoveTask: handleMoveTask,
            onVisibleRangeChange: (range) => {
              setCalendarRange((prev) => prev.start === range.start && prev.end === range.end ? prev : range);
            },
          })
        )}
      </div>

      {selectedTask && (
        <TaskDetail
          task={selectedTask}
          categories={categories}
          onUpdate={handleUpdateTask}
          onDelete={handleDeleteTask}
          onClose={() => setSelectedTask(null)}
        />
      )}

      {(selectedEvent || eventDraft) && (
        <EventDetail
          mode={selectedEvent ? 'edit' : 'create'}
          draft={selectedEvent ? eventToDraft(selectedEvent) : eventDraft || getEmptyEventDraft()}
          tasks={tasks.filter((task) => task.status !== 'done')}
          onSave={handleSaveEvent}
          onDelete={selectedEvent ? handleDeleteEvent : undefined}
          onClose={() => {
            setSelectedEvent(null);
            setEventDraft(null);
          }}
        />
      )}

      {commandBarOpen && (
        <CommandBar
          tasks={tasks}
          events={events}
          notes={recentNotes}
          onClose={() => setCommandBarOpen(false)}
          onSelectTask={(task) => {
            setSelectedTask(task);
            setSelectedEvent(null);
            setEventDraft(null);
            setCommandBarOpen(false);
          }}
          onSelectEvent={(event) => {
            setSelectedEvent(event);
            setSelectedTask(null);
            setEventDraft(null);
            setCommandBarOpen(false);
          }}
          onOpenNote={(date) => {
            setActiveNoteDate(date);
            window.location.hash = '#notes';
            setCommandBarOpen(false);
          }}
          onAddTask={(title) => {
            void handleAddTask(title);
            setCommandBarOpen(false);
          }}
          onCaptureNote={(text) => {
            void handleCaptureNote(text);
            setCommandBarOpen(false);
          }}
          onAddEventStub={(title) => {
            void handleAddEventStub(title);
            setCommandBarOpen(false);
          }}
          onNavigate={(action) => {
            if (action === 'filter-pending') {
              setStatusFilter('pending');
            }
            if (action === 'filter-all') {
              setStatusFilter(null);
            }
            if (action === 'view-today') {
              window.location.hash = '#today';
            }
            if (action === 'view-calendar') {
              window.location.hash = '#calendar';
            }
            if (action === 'view-notes') {
              window.location.hash = '#notes';
            }
            if (action === 'view-review') {
              window.location.hash = '#review';
            }
            setCommandBarOpen(false);
          }}
        />
      )}

      <ToastContainer toasts={toasts} />
    </div>
  );
}

function renderMainView({
  view,
  tasks,
  events,
  activeNoteDate,
  activeNote,
  todayNote,
  recentNotes,
  completedThisWeek,
  overdueTasks,
  notesThisWeek,
  onSaveNote,
  onSelectTask,
  onSelectEvent,
  onOpenNotes,
  onOpenNote,
  onChangeNoteDate,
  onRequestCreateEvent,
  onMoveEvent,
  onMoveTask,
  onVisibleRangeChange,
}: {
  view: AppView;
  tasks: TaskRecord[];
  events: EventRecord[];
  activeNoteDate: string;
  activeNote: DailyNoteRecord | null;
  todayNote: DailyNoteRecord | null;
  recentNotes: DailyNoteRecord[];
  completedThisWeek: TaskRecord[];
  overdueTasks: TaskRecord[];
  notesThisWeek: DailyNoteRecord[];
  onSaveNote: (date: string, updates: { title: string; content: string }) => Promise<void>;
  onSelectTask: (task: TaskRecord) => void;
  onSelectEvent: (event: EventRecord) => void;
  onOpenNotes: () => void;
  onOpenNote: (date: string) => void;
  onChangeNoteDate: (date: string) => void;
  onRequestCreateEvent: (draft: EventEditorDraft) => void;
  onMoveEvent: (event: EventRecord, updates: { event_date: string; start_time: string | null; end_time: string | null; all_day: boolean }) => Promise<void>;
  onMoveTask: (task: TaskRecord, updates: { due_date: string; scheduled_start: string | null; scheduled_end: string | null }) => Promise<void>;
  onVisibleRangeChange: (range: { start: string; end: string }) => void;
}) {
  switch (view) {
    case 'calendar':
      return (
        <CalendarView
          tasks={tasks}
          events={events}
          onRequestCreateEvent={onRequestCreateEvent}
          onSelectTask={onSelectTask}
          onSelectEvent={onSelectEvent}
          onMoveEvent={onMoveEvent}
          onMoveTask={onMoveTask}
          onVisibleRangeChange={onVisibleRangeChange}
        />
      );
    case 'notes':
      return (
        <NotesView
          currentDate={activeNoteDate}
          note={activeNote}
          recentNotes={recentNotes}
          onChangeDate={onChangeNoteDate}
          onSaveNote={onSaveNote}
        />
      );
    case 'review':
      return (
        <ReviewView
          completedTasks={completedThisWeek}
          recentNotes={notesThisWeek}
          overdueTasks={overdueTasks}
          onSelectTask={onSelectTask}
          onOpenNote={onOpenNote}
        />
      );
    case 'today':
    default:
      return (
        <TodayView
          tasks={tasks}
          events={events}
          todayNote={todayNote}
          onSaveNote={onSaveNote}
          onSelectTask={onSelectTask}
          onSelectEvent={onSelectEvent}
          onOpenNotes={onOpenNotes}
        />
      );
  }
}

function getViewFromHash(hash: string): AppView {
  const next = hash.replace('#', '');
  if (next === 'calendar' || next === 'notes' || next === 'review') {
    return next;
  }
  return 'today';
}

function getDefaultCalendarRange(): { start: string; end: string } {
  const today = formatDateKey(new Date());
  return {
    start: addDays(today, -14),
    end: addDays(today, 30),
  };
}

function eventToDraft(event: EventRecord): EventEditorDraft {
  return {
    title: event.title,
    description: event.description || '',
    eventDate: event.eventDate,
    startTime: event.startTime || '',
    endTime: event.endTime || '',
    allDay: event.allDay,
    color: event.color || '#3b82f6',
    taskId: event.taskId,
  };
}

function getEmptyEventDraft(): EventEditorDraft {
  return {
    title: '',
    description: '',
    eventDate: formatDateKey(new Date()),
    startTime: '',
    endTime: '',
    allDay: true,
    color: '#3b82f6',
    taskId: null,
  };
}
